import * as React from "react";
import { connect } from "react-redux";
import { ColorResult } from "react-color";
import { WrappedFieldProps } from "redux-form";

import { RootState, Dispatch } from "../../../store/types";
import { Color } from "../../../models";
import { ColorChoiceState as State } from "../reducer";
import * as actions from "../actions";
import * as selectors from "../selectors";
import { LoadingSpinner } from "../../../components";
import ColorPicker from "./ColorPicker";
import ColorItem from "./ColorItem";
import CustomColorItem from "./CustomColorItem";
import CustomColorSelector from "./CustomColorSelector";
import ColorField from "./ColorField";

export interface ColorChoiceProps extends State {
  primary_color: WrappedFieldProps;
  secondary_color: WrappedFieldProps;
  fetchColors: () => void;
  chooseColor: (c?: Color) => void;
  toggleCheckbox: () => void;
}

interface ColorChoiceState {
  showPicker: boolean;
  showCustomPrimary: boolean;
  showCustomSecondary: boolean;
  color?: Color;
}

class ColorChoiceContainer extends React.Component<
  ColorChoiceProps,
  ColorChoiceState
> {
  public state: ColorChoiceState = {
    showPicker: false,
    showCustomPrimary: false,
    showCustomSecondary: false,
  };

  public componentDidMount() {
    this.props.fetchColors();
  }

  private togglePicker = () =>
    this.setState(state => ({ ...state, showPicker: !state.showPicker }));

  private toggleCustom = (
    stateKey: "showCustomPrimary" | "showCustomSecondary",
  ) => (flag: boolean) => () =>
    this.setState(state => ({ ...state, [stateKey]: !state[stateKey] }));

  private handleLocalUpdate = (key: "primary_color" | "secondary_color") => (
    color: ColorResult,
  ) =>
    this.setState(state => ({
      ...state,
      color: { ...state.color, [key]: color.hex },
    }));

  private bindLocalUpdate = (color: Color) => () => {
    this.setState(state => ({ ...state, color }));
    // switch off custom color if currently checked
    if (this.props.customColorsChecked) {
      this.props.toggleCheckbox();
    }
  };

  private handleConfirm = (e: React.SyntheticEvent<any>) => {
    if (this.state.color) {
      this.props.primary_color.input.onChange(this.state.color.primary_color);
      this.props.secondary_color.input.onChange(
        this.state.color.secondary_color,
      );
    }

    this.props.chooseColor(this.state.color);
    this.togglePicker();
  };

  private renderColors() {
    if (this.props.isColorsLoading) {
      return <LoadingSpinner large />;
    }

    return this.props.colors.map(color => (
      <ColorItem
        {...color}
        key={color.id}
        onClick={this.bindLocalUpdate(color)}
        active={this.getColorChosenState(color)}
      />
    ));
  }

  private renderCustomSelector = () => (
    <CustomColorSelector key="custom" {...this.props}>
      <CustomColorItem
        key="primary"
        color={this.state.color ? this.state.color.primary_color : undefined}
        title="Màu chính"
        onUpdate={this.handleLocalUpdate("primary_color")}
        setPickerShow={this.toggleCustom("showCustomPrimary")}
        isPickerShowing={this.state.showCustomPrimary}
      />
      <CustomColorItem
        key="secondary"
        color={this.state.color ? this.state.color.secondary_color : undefined}
        title="Màu phụ"
        onUpdate={this.handleLocalUpdate("secondary_color")}
        setPickerShow={this.toggleCustom("showCustomSecondary")}
        isPickerShowing={this.state.showCustomSecondary}
      />
    </CustomColorSelector>
  );

  private renderError() {
    const { primary_color: prim, secondary_color: sec } = this.props;

    if (prim.meta.touched && prim.meta.error) return prim.meta.error;
    if (prim.meta.touched && sec.meta.error) return sec.meta.error;
    return;
  }

  private getColorChosenState = (color: Color): boolean =>
    this.state.color && !this.props.customColorsChecked
      ? this.state.color.id === color.id
      : false;

  public render(): any {
    return [
      <ColorField
        key="picker-trigger"
        label="Màu sắc"
        onClick={this.togglePicker}
        error={this.renderError()}
        chosenColor={this.props.chosenColor}
      />,
      <ColorPicker
        key="picker-modal"
        show={this.state.showPicker}
        customPicker={this.renderCustomSelector()}
        onConfirm={this.handleConfirm}
        onCancel={this.togglePicker}
      >
        {this.renderColors()}
      </ColorPicker>,
    ];
  }
}

const mapStateToProps = (state: RootState): Partial<ColorChoiceProps> => ({
  colors: selectors.colors(state),
  isColorsLoading: selectors.isColorsLoading(state),
  chosenColor: selectors.chosenColor(state),
  customColorsChecked: selectors.checkboxChecked(state),
});

const mapDispatchToProps = (dispatch: Dispatch): Partial<ColorChoiceProps> => ({
  fetchColors: () => dispatch(actions.fetchColors()),
  chooseColor: (c?: Color) => dispatch(actions.setChosenColor(c)),
  toggleCheckbox: () => dispatch(actions.toggleCheckbox()),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  ColorChoiceContainer,
);



// WEBPACK FOOTER //
// ./src/routes/WizardColorChoice/components/ColorChoiceContainer.tsx